import videoUrlParser from 'js-video-url-parser';
import YouTubeExtend from '@/javascripts/js-video-url-parser/provider/youtube';

videoUrlParser.plugins.youtube = new YouTubeExtend();

/**
 * CKEditor 出力中の動画埋め込み（oembed[url] / YouTube Shorts リンク）を iframe に置換する。
 *
 * rootElement を渡すとその配下だけを走査する。省略時は document 全体。
 * MutationObserver で追加されたノードなど、部分的な再処理はスコープを渡して呼ぶ。
 */
class ViewMode {
  private readonly rootElement: ParentNode;

  constructor(rootElement: ParentNode = document) {
    this.rootElement = rootElement;
  }

  replace(): void {
    this.rootElement.querySelectorAll<HTMLElement>('oembed[url]').forEach((elem) => {
      const url = elem.getAttribute('url');
      if (!url) return;

      const videoInfo = videoUrlParser.parse(url);
      if (!videoInfo) return;

      // 親の innerHTML を書き換えると同じ親にある未処理の oembed まで消えるため、対象要素だけを置換する
      switch (videoInfo.provider) {
        case 'vimeo':
          elem.replaceWith(this.createVideoBlock(`//player.vimeo.com/video/${videoInfo.id}`));
          break;
        case 'youtube':
          elem.replaceWith(this.createVideoBlock(`//www.youtube.com/embed/${videoInfo.id}`));
          break;
      }
    });

    this.rootElement
      .querySelectorAll<HTMLAnchorElement>('figure.media a[href*="youtube.com/shorts/"]')
      .forEach((elem) => {
        const url = elem.getAttribute('href');
        const videoId = url ? this.extractYouTubeVideoId(url) : null;
        if (!videoId) return;

        const figureElement = elem.closest('figure.media');
        if (figureElement) {
          figureElement.replaceWith(this.createYouTubeShortsBlock(videoId));
        }
      });
  }

  /** YouTube の動画 ID は 11 文字の [A-Za-z0-9_-]。末尾のスラッシュやクエリを拾わないよう長さで区切る */
  extractYouTubeVideoId(url: string): string | null {
    const match = url.match(/youtube\.com\/shorts\/([\w-]{11})/);
    return match ? match[1] : null;
  }

  createYouTubeShortsBlock(videoId: string): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = 'position: relative; padding-bottom: 56.2493%; height: 0;';

    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', `https://www.youtube.com/embed/${videoId}`);
    iframe.style.cssText = 'position: absolute; width: 100%; height: 100%; top: 0; left: 0;';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; encrypted-media');
    iframe.setAttribute('allowfullscreen', '');

    container.appendChild(iframe);
    return container;
  }

  createVideoBlock(url: string): HTMLElement {
    const videoContainer = document.createElement('div');
    const iframe = document.createElement('iframe');
    iframe.setAttribute('width', '100%');
    iframe.setAttribute('height', '100%');
    iframe.setAttribute('src', url);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    videoContainer.className = 'video-container';
    videoContainer.appendChild(iframe);
    return videoContainer;
  }
}

export default ViewMode;
